import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Zap, Wrench, Clock, Shield, Package, CheckCircle, ArrowRight, ArrowLeft,
  Gamepad2, Cpu, User, Phone, Mail, MapPin, AlertCircle, Loader,
  FileText, Truck, Check,
} from '../lib/icons';
import SEOHead from '../components/SEOHead';
import { getCachedSheetData } from '../lib/sheets-sync';
import { DEVICE_CATEGORY_LABELS } from '../types/services';

// ─── Constants ────────────────────────────────────────────────────────────────

const SHIP_TO = {
  company:  'GameFix Solutions',
  attn:     'Spencer Parkes',
  address1: '30401 Cupeno Ln',
  city:     'Temecula',
  state:    'CA',
  zip:      '92592',
};

const scrollToIntake = () =>
  document.getElementById('mail-in-intake')?.scrollIntoView({ behavior: 'smooth' });

// ─── Marketing sub-components ─────────────────────────────────────────────────

const Badge = ({ text }: { text: string }) => (
  <span className="inline-block bg-[#39FF14]/20 text-[#39FF14] text-xs font-semibold px-3 py-1 rounded-full border border-[#39FF14]/40 mb-4">
    {text}
  </span>
);

const StatBadge = ({ value, label }: { value: string; label: string }) => (
  <div className="text-center">
    <div className="text-3xl font-bold text-[#39FF14]">{value}</div>
    <div className="text-sm text-[#B0B0B0] mt-1">{label}</div>
  </div>
);

const RepairCard = ({ icon: Icon, title, price, time, link }: { icon: React.ElementType; title: string; price: string; time: string; link?: string }) => {
  const inner = (
    <div className={`bg-gray-800/60 border rounded-xl p-5 flex items-center gap-4 transition-colors ${link ? 'border-gray-700 hover:border-[#39FF14]/60 hover:bg-gray-800/90 cursor-pointer' : 'border-gray-700 hover:border-[#00BFFF]/50'}`}>
      <div className="bg-[#00BFFF]/10 p-3 rounded-lg shrink-0">
        <Icon className="h-5 w-5 text-[#00BFFF]" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[#E0E0E0] font-medium">{title}</p>
        <p className="text-xs text-[#B0B0B0] mt-0.5">{time}</p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className="text-[#39FF14] font-bold">{price}</span>
        {link && <span className="text-[#39FF14] text-xs font-semibold">→</span>}
      </div>
    </div>
  );
  return link ? <Link to={link}>{inner}</Link> : inner;
};

// ─── Intake types ─────────────────────────────────────────────────────────────

interface CustFields   { firstName: string; lastName: string; phone: string; email: string }
interface DevFields    { categoryId: string; model: string; serviceId: string; serviceName: string; servicePrice: number | null; issueDesc: string; whatHappened: string; whatTried: string }
interface ReturnFields { sameAsContact: boolean; fullName: string; address1: string; address2: string; city: string; state: string; zip: string; phone: string }
interface SubmittedState { ticketNumber: number | null; fallbackId: string; deviceLabel: string; serviceName: string }

// ─── Helpers ──────────────────────────────────────────────────────────────────

const inp = (err?: string) =>
  `w-full bg-gray-800 border ${err ? 'border-[#FF1744]' : 'border-gray-700'} rounded-xl px-4 py-3 text-[#E0E0E0] text-sm focus:outline-none focus:border-[#39FF14] transition-colors`;

const lbl = 'block text-sm font-medium text-[#B0B0B0] mb-1.5';

const FieldErr = ({ msg }: { msg?: string }) =>
  msg ? <p className="text-xs text-[#FF1744] mt-1.5 flex items-center gap-1"><AlertCircle className="h-3 w-3 shrink-0" />{msg}</p> : null;

// ─── Step indicator ───────────────────────────────────────────────────────────

const STEP_LABELS = ['Your Info', 'Device & Issue', 'Return Address', 'Review'];

function StepIndicator({ step }: { step: number }) {
  return (
    <div className="flex items-center justify-center gap-0 mb-10">
      {STEP_LABELS.map((label, i) => {
        const n = i + 1;
        const done    = n < step;
        const current = n === step;
        return (
          <React.Fragment key={n}>
            <div className="flex flex-col items-center gap-1.5">
              <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all ${done ? 'bg-[#39FF14] text-[#0D0D0D]' : current ? 'bg-[#39FF14] text-[#0D0D0D] ring-4 ring-[#39FF14]/20' : 'bg-gray-800 text-[#B0B0B0] border border-gray-700'}`}>
                {done ? <Check className="h-4 w-4" /> : n}
              </div>
              <span className={`text-xs hidden sm:block ${current ? 'text-[#39FF14] font-semibold' : done ? 'text-[#B0B0B0]' : 'text-gray-600'}`}>{label}</span>
            </div>
            {i < STEP_LABELS.length - 1 && (
              <div className={`h-px w-10 sm:w-14 mx-1 mb-5 transition-colors ${done ? 'bg-[#39FF14]' : 'bg-gray-700'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── Shipping help ────────────────────────────────────────────────────────────

const SHIPPING_TIPS = [
  'Use a box only large enough to safely protect the device.',
  'Leave adequate padding around the console or device.',
  'Measure the final box after packing.',
  'Weigh the complete packed box, not just the device.',
  'Compare USPS and UPS before purchasing the label.',
  'Use a service that includes tracking.',
  'Consider insurance for higher-value devices.',
  'Keep your tracking number until the repair is complete.',
];

function ShippingHelpSection() {
  return (
    <section className="py-16 px-4 border-b border-gray-800 bg-gray-900/20">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-start gap-4 mb-6">
          <div className="bg-[#39FF14]/10 p-4 rounded-xl shrink-0">
            <Truck className="h-8 w-8 text-[#39FF14]" />
          </div>
          <div>
            <h2 className="text-2xl font-bold mb-2">Save Money on Shipping</h2>
            <p className="text-[#B0B0B0]">
              Shipping a console or handheld doesn't have to be expensive. Before purchasing postage at a retail counter, we recommend comparing discounted online shipping rates.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          <div className="bg-gray-900 border border-[#39FF14]/30 rounded-2xl p-5 flex flex-col">
            <div className="flex items-center gap-2 mb-2">
              <span className="bg-[#39FF14]/15 text-[#39FF14] text-xs font-bold px-2.5 py-0.5 rounded-full">Recommended</span>
            </div>
            <h3 className="font-bold text-[#E0E0E0] mb-2">Pirate Ship</h3>
            <p className="text-sm text-[#B0B0B0] mb-4 flex-1">
              Compare discounted USPS and UPS rates and purchase your label online. Pirate Ship has no monthly membership fee.
            </p>
            <a
              href="https://www.pirateship.com/ship"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full bg-[#39FF14] hover:bg-[#00BFFF] text-[#0D0D0D] font-bold text-sm px-4 py-2.5 rounded-xl transition-colors text-center block"
            >
              Compare Rates with Pirate Ship
            </a>
          </div>

          <div className="bg-gray-900 border border-gray-700 rounded-2xl p-5 flex flex-col">
            <h3 className="font-bold text-[#E0E0E0] mb-2 mt-7">PayPal Shipping</h3>
            <p className="text-sm text-[#B0B0B0] mb-4 flex-1">
              If you already have a PayPal account, PayPal Shipping Center can also provide discounted shipping labels through carriers including USPS and UPS.
            </p>
            <a
              href="https://www.paypal.com/shipnow"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full border border-gray-600 hover:border-[#39FF14]/50 text-[#B0B0B0] hover:text-[#E0E0E0] font-semibold text-sm px-4 py-2.5 rounded-xl transition-colors text-center block"
            >
              Open PayPal Shipping Center
            </a>
          </div>
        </div>

        <div className="bg-gray-900/60 border border-gray-700 rounded-2xl p-6">
          <h3 className="font-semibold text-[#E0E0E0] mb-4">Tips to Keep Shipping Costs Down</h3>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2.5">
            {SHIPPING_TIPS.map(tip => (
              <li key={tip} className="flex items-start gap-2.5 text-sm text-[#B0B0B0]">
                <div className="w-1.5 h-1.5 rounded-full bg-[#39FF14]/60 mt-1.5 shrink-0" />
                {tip}
              </li>
            ))}
          </ul>
          <p className="text-xs text-gray-600 mt-4 italic">
            Shipping cost depends on your location, package dimensions, weight, carrier, service level, and insurance. Use the rate calculators above for an accurate quote.
          </p>
        </div>

        <p className="text-xs text-gray-600 mt-4 text-center">
          GameFix Solutions is not affiliated with Pirate Ship or PayPal. These are independent services — you purchase and manage your own inbound shipping label.
        </p>
      </div>
    </section>
  );
}

function ShippingHelpConfirmation() {
  return (
    <div className="bg-gray-900 border border-gray-700 rounded-2xl p-6">
      <p className="text-xs font-semibold text-[#B0B0B0] uppercase tracking-widest mb-3 flex items-center gap-2">
        <Truck className="h-4 w-4" /> Save on Shipping
      </p>
      <p className="text-sm text-[#B0B0B0] mb-4">
        Want to save on shipping? Before purchasing postage at a retail counter, compare discounted rates online.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <a
          href="https://www.pirateship.com/ship"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 bg-[#39FF14]/10 hover:bg-[#39FF14]/20 border border-[#39FF14]/30 text-[#39FF14] font-semibold text-sm px-4 py-2.5 rounded-xl transition-colors text-center"
        >
          Compare Rates — Pirate Ship
        </a>
        <a
          href="https://www.paypal.com/shipnow"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 border border-gray-700 hover:border-gray-500 text-[#B0B0B0] hover:text-[#E0E0E0] font-semibold text-sm px-4 py-2.5 rounded-xl transition-colors text-center"
        >
          PayPal Shipping Center
        </a>
      </div>
      <p className="text-xs text-gray-600 mt-3 italic">
        These are independent services. You purchase and manage your own inbound shipping label.
      </p>
    </div>
  );
}

// ─── Confirmation view ────────────────────────────────────────────────────────

function ConfirmationView({
  submitted, cust, dev, ret,
}: {
  submitted: SubmittedState;
  cust: CustFields; dev: DevFields; ret: ReturnFields;
}) {
  const tn = submitted.ticketNumber ? `#${submitted.ticketNumber}` : submitted.fallbackId;
  const returnName  = ret.sameAsContact ? `${cust.firstName} ${cust.lastName}`.trim() : ret.fullName;
  const returnPhone = ret.sameAsContact ? cust.phone : ret.phone;

  function handlePrint() {
    const w = window.open('', '_blank', 'width=720,height=960');
    if (!w) return;
    w.document.write(`<!DOCTYPE html><html><head><title>GameFix Mail-In — Ticket ${tn}</title>
<style>
  body{font-family:Arial,sans-serif;padding:40px;color:#111;max-width:600px;margin:0 auto}
  h1{font-size:20px;margin-bottom:4px;color:#0D0D0D}
  h2{font-size:14px;font-weight:bold;text-transform:uppercase;letter-spacing:1px;color:#555;border-bottom:2px solid #39FF14;padding-bottom:6px;margin-top:28px}
  .ticket{font-size:30px;font-weight:900;color:#0D0D0D;background:#39FF14;padding:6px 18px;display:inline-block;border-radius:6px;margin:12px 0 24px}
  address{font-style:normal;font-size:15px;line-height:1.9;background:#f4f4f4;padding:16px 20px;border-radius:8px;border-left:4px solid #39FF14}
  ul{padding-left:20px;line-height:2;color:#333}
  .grid{display:grid;grid-template-columns:1fr 1fr;gap:12px 24px;margin:16px 0}
  .f .k{font-size:11px;text-transform:uppercase;letter-spacing:0.5px;color:#888;margin-bottom:2px}
  .f .v{font-size:14px;font-weight:bold}
  @media print{body{padding:20px}}
</style></head><body>
<h1>GameFix Solutions — Mail-In Repair</h1>
<div class="ticket">Ticket ${tn}</div>
<div class="grid">
  <div class="f"><div class="k">Customer</div><div class="v">${cust.firstName} ${cust.lastName}</div></div>
  <div class="f"><div class="k">Phone</div><div class="v">${cust.phone}</div></div>
  <div class="f"><div class="k">Email</div><div class="v">${cust.email}</div></div>
  <div class="f"><div class="k">Device</div><div class="v">${submitted.deviceLabel}${dev.model ? ` — ${dev.model}` : ''}</div></div>
  <div class="f"><div class="k">Service</div><div class="v">${submitted.serviceName || 'Mail-In Repair'}</div></div>
</div>
${returnName ? `<div class="f" style="margin-bottom:20px"><div class="k">Return shipping address</div><div class="v" style="font-weight:normal;line-height:1.7">${returnName}<br>${ret.address1}${ret.address2 ? ' ' + ret.address2 : ''}<br>${ret.city}, ${ret.state} ${ret.zip}<br>${returnPhone}</div></div>` : ''}
<h2>Ship Your Device To</h2>
<address>
  <strong>${SHIP_TO.company}</strong><br>
  Attn: ${SHIP_TO.attn} / Ticket ${tn}<br>
  ${SHIP_TO.address1}<br>
  ${SHIP_TO.city}, ${SHIP_TO.state} ${SHIP_TO.zip}
</address>
<h2>Packing Instructions</h2>
<ul>
  <li>Write ticket number <strong>${tn}</strong> on a note and place it inside the box</li>
  <li>Wrap the device securely with bubble wrap, foam, or packing peanuts</li>
  <li>Do not ship the device loose inside the box</li>
  <li>Include accessories only if relevant to the issue or specifically requested</li>
  <li>Use a shipping service with tracking — keep your tracking number</li>
  <li>We recommend insuring higher-value devices for their replacement value</li>
</ul>
<p style="font-size:12px;color:#888;margin-top:28px">GameFix Solutions is not responsible for packages before delivery to our Temecula location.</p>
<script>window.print();</script>
</body></html>`);
    w.document.close();
  }

  return (
    <div className="space-y-6">
      {/* Headline */}
      <div className="text-center">
        <div className="w-16 h-16 rounded-full bg-[#39FF14]/15 border-2 border-[#39FF14] flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="h-8 w-8 text-[#39FF14]" />
        </div>
        <h2 className="text-3xl font-bold mb-2">Your Mail-In Repair Request Has Been Submitted</h2>
        {!submitted.ticketNumber && (
          <p className="text-sm text-[#FFD700] mt-2">Our RepairShopr system encountered an issue — your request was captured and we'll confirm your official ticket number separately.</p>
        )}
      </div>

      {/* Ticket number */}
      <div className="bg-gray-900 border border-[#39FF14]/40 rounded-2xl p-6 text-center">
        <p className="text-sm font-semibold text-[#B0B0B0] uppercase tracking-widest mb-2">Your Ticket Number</p>
        <div className="text-5xl font-black text-[#39FF14] tracking-tight mb-3">
          {submitted.ticketNumber ? `#${submitted.ticketNumber}` : submitted.fallbackId}
        </div>
        <p className="text-sm text-[#B0B0B0]">
          Your repair request has been created successfully. Please include your ticket number with your device so we can quickly identify it when it arrives.
        </p>
      </div>

      {/* Ship to */}
      <div className="bg-gray-900 border border-[#39FF14]/30 rounded-2xl p-6">
        <p className="text-xs font-semibold text-[#39FF14] uppercase tracking-widest mb-4 flex items-center gap-2">
          <Package className="h-4 w-4" /> Ship Your Device To
        </p>
        <div className="font-mono text-[#E0E0E0] text-lg leading-8 bg-[#0D0D0D] rounded-xl p-5 border border-gray-800">
          <span className="font-bold">{SHIP_TO.company}</span><br />
          Attn: {SHIP_TO.attn} / Ticket {submitted.ticketNumber ? `#${submitted.ticketNumber}` : submitted.fallbackId}<br />
          {SHIP_TO.address1}<br />
          {SHIP_TO.city}, {SHIP_TO.state} {SHIP_TO.zip}
        </div>
        <p className="text-xs text-[#B0B0B0] mt-3">This is our Temecula location and the receiving address for GameFix Solutions mail-in repairs.</p>
      </div>

      {/* Return address */}
      {(ret.address1 || ret.sameAsContact) && (
        <div className="bg-gray-900 border border-gray-700 rounded-2xl p-5">
          <p className="text-xs font-semibold text-[#B0B0B0] uppercase tracking-widest mb-3 flex items-center gap-2">
            <Truck className="h-4 w-4" /> We'll Return Your Device To
          </p>
          <p className="text-[#E0E0E0]">{returnName}</p>
          <p className="text-[#B0B0B0] text-sm">{ret.address1}{ret.address2 ? `, ${ret.address2}` : ''}</p>
          <p className="text-[#B0B0B0] text-sm">{ret.city}, {ret.state} {ret.zip}</p>
          <p className="text-[#B0B0B0] text-sm">{returnPhone}</p>
        </div>
      )}

      {/* Packing checklist */}
      <div className="bg-gray-900 border border-gray-700 rounded-2xl p-6">
        <p className="text-xs font-semibold text-[#B0B0B0] uppercase tracking-widest mb-4">Packing Checklist</p>
        <ul className="space-y-3">
          {[
            `Write your ticket number (${tn}) on a note and place it inside the box`,
            'Wrap the device securely — bubble wrap, foam, or packing peanuts',
            'Do not ship the device loose inside the box',
            'Include accessories only if relevant to the issue or specifically requested',
            'If the issue involves charging or power, include the adapter',
            'Use a shipping service with tracking — keep your tracking number',
            'We recommend insuring higher-value devices for their replacement value',
          ].map(item => (
            <li key={item} className="flex items-start gap-3 text-sm text-[#B0B0B0]">
              <div className="w-5 h-5 rounded-full bg-[#39FF14]/15 border border-[#39FF14]/40 flex items-center justify-center shrink-0 mt-0.5">
                <Check className="h-3 w-3 text-[#39FF14]" />
              </div>
              {item}
            </li>
          ))}
        </ul>
        <p className="text-xs text-gray-600 mt-4 italic">
          GameFix Solutions is not responsible for packages before delivery to our Temecula location.
        </p>
      </div>

      {/* Shipping help */}
      <ShippingHelpConfirmation />

      {/* Print button */}
      <button
        onClick={handlePrint}
        className="w-full border border-gray-700 hover:border-[#39FF14]/50 text-[#B0B0B0] hover:text-[#E0E0E0] font-semibold px-6 py-3.5 rounded-xl transition-colors flex items-center justify-center gap-2"
      >
        <FileText className="h-4 w-4" />
        Print Mail-In Instructions
      </button>

      <p className="text-center text-sm text-[#B0B0B0]">
        A confirmation email and SMS have been sent to <span className="text-[#E0E0E0]">{cust.email}</span>.
      </p>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function MailInRepairsPage() {
  // Sheet services
  const sheetServices = useMemo(() => {
    const cached = getCachedSheetData();
    if (!cached) return [];
    return cached.services.filter(s => s.status === 'active');
  }, []);

  const availableCategories = useMemo(() => {
    const seen = new Set(sheetServices.map(s => s.deviceCategory));
    return (Object.entries(DEVICE_CATEGORY_LABELS) as [string, string][]).filter(([id]) => seen.has(id));
  }, [sheetServices]);

  const categoriesForSelect: [string, string][] =
    availableCategories.length > 0
      ? availableCategories
      : (Object.entries(DEVICE_CATEGORY_LABELS) as [string, string][]);

  // Intake state
  const [step, setStep]           = useState<1|2|3|4>(1);
  const [submitted, setSubmitted] = useState<SubmittedState | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors]       = useState<Record<string, string>>({});

  const [cust, setCust] = useState<CustFields>({ firstName: '', lastName: '', phone: '', email: '' });
  const [dev, setDev]   = useState<DevFields>({ categoryId: '', model: '', serviceId: '', serviceName: '', servicePrice: null, issueDesc: '', whatHappened: '', whatTried: '' });
  const [ret, setRet]   = useState<ReturnFields>({ sameAsContact: false, fullName: '', address1: '', address2: '', city: '', state: '', zip: '', phone: '' });

  const servicesForCat = useMemo(
    () => sheetServices.filter(s => s.deviceCategory === dev.categoryId),
    [sheetServices, dev.categoryId],
  );

  function setC(k: keyof CustFields, v: string) { setCust(p => ({ ...p, [k]: v })); setErrors(e => ({ ...e, [k]: '' })); }
  function setD(k: keyof DevFields, v: any)      { setDev(p => ({ ...p, [k]: v })); setErrors(e => ({ ...e, [k]: '' })); }
  function setR(k: keyof ReturnFields, v: any)   { setRet(p => ({ ...p, [k]: v })); setErrors(e => ({ ...e, [k]: '' })); }

  function handleCatChange(catId: string) {
    setDev(p => ({ ...p, categoryId: catId, serviceId: '', serviceName: '', servicePrice: null }));
    setErrors(e => ({ ...e, categoryId: '' }));
  }

  function handleServiceChange(serviceId: string) {
    const svc = servicesForCat.find(s => s.id === serviceId);
    setDev(p => ({
      ...p,
      serviceId,
      serviceName: svc?.repairType ?? '',
      servicePrice: svc?.basePrice ?? null,
    }));
  }

  function handleSameAsContact(checked: boolean) {
    if (checked) {
      setRet(p => ({ ...p, sameAsContact: true, fullName: `${cust.firstName} ${cust.lastName}`.trim(), phone: cust.phone }));
    } else {
      setRet(p => ({ ...p, sameAsContact: false }));
    }
  }

  // ─── Validation ────────────────────────────────────────────────────────────

  function validateStep1(): boolean {
    const e: Record<string, string> = {};
    if (!cust.firstName.trim()) e.firstName = 'First name is required';
    if (!cust.lastName.trim())  e.lastName  = 'Last name is required';
    if (!cust.phone.trim())     e.phone     = 'Phone number is required';
    else if (!/^\+?[\d\s\-(). ]{7,}$/.test(cust.phone)) e.phone = 'Enter a valid phone number';
    if (!cust.email.trim())     e.email     = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cust.email)) e.email = 'Enter a valid email address';
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  function validateStep2(): boolean {
    const e: Record<string, string> = {};
    if (!dev.categoryId)                        e.categoryId = 'Please select a device type';
    if (dev.issueDesc.trim().length < 5)        e.issueDesc  = 'Please describe the issue';
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  function validateStep3(): boolean {
    const e: Record<string, string> = {};
    if (!ret.sameAsContact) {
      if (!ret.fullName.trim()) e.fullName = 'Full name is required';
      if (!ret.phone.trim())    e.phone    = 'Phone number is required';
    }
    if (!ret.address1.trim()) e.address1 = 'Street address is required';
    if (!ret.city.trim())     e.city     = 'City is required';
    if (!ret.state.trim())    e.state    = 'State is required';
    if (!ret.zip.trim())      e.zip      = 'ZIP code is required';
    else if (!/^\d{5}(-\d{4})?$/.test(ret.zip.trim())) e.zip = 'Enter a valid 5-digit ZIP code';
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  function nextStep() {
    const valid =
      step === 1 ? validateStep1() :
      step === 2 ? validateStep2() :
      step === 3 ? validateStep3() : true;
    if (valid) {
      setErrors({});
      setStep(s => (s + 1) as 1|2|3|4);
      document.getElementById('mail-in-intake')?.scrollIntoView({ behavior: 'smooth' });
    }
  }

  function prevStep() {
    setErrors({});
    setStep(s => Math.max(1, s - 1) as 1|2|3|4);
    document.getElementById('mail-in-intake')?.scrollIntoView({ behavior: 'smooth' });
  }

  // ─── Submit ────────────────────────────────────────────────────────────────

  async function handleSubmit() {
    setSubmitting(true);
    const fallbackId = `GFX-${Date.now().toString(36).toUpperCase()}`;
    const deviceLabel = DEVICE_CATEGORY_LABELS[dev.categoryId as keyof typeof DEVICE_CATEGORY_LABELS] || dev.categoryId;

    const returnAddr = {
      full_name: ret.sameAsContact ? `${cust.firstName} ${cust.lastName}`.trim() : ret.fullName,
      address1:  ret.address1,
      address2:  ret.address2,
      city:      ret.city,
      state:     ret.state,
      zip:       ret.zip,
      phone:     ret.sameAsContact ? cust.phone : ret.phone,
    };

    const notesLines = [
      'MAIL-IN REPAIR REQUEST',
      '══════════════════════',
      `Device: ${deviceLabel}${dev.model ? ` — ${dev.model}` : ''}`,
      `Service: ${dev.serviceName || 'To be determined'}`,
      dev.servicePrice !== null ? `Price: $${dev.servicePrice}` : 'Pricing: Quote after inspection',
      '',
      'ISSUE DESCRIPTION',
      dev.issueDesc,
    ];
    if (dev.whatHappened) notesLines.push('', 'HOW IT STARTED', dev.whatHappened);
    if (dev.whatTried)    notesLines.push('', 'WHAT CUSTOMER TRIED', dev.whatTried);
    notesLines.push(
      '',
      'RETURN SHIPPING ADDRESS',
      returnAddr.full_name,
      `${returnAddr.address1}${returnAddr.address2 ? `, ${returnAddr.address2}` : ''}`,
      `${returnAddr.city}, ${returnAddr.state} ${returnAddr.zip}`,
      returnAddr.phone,
      '',
      '──────────────────────',
      `Submitted: ${new Date().toLocaleString()}`,
      'Via: gamefixsolutions.com/mail-in-repairs',
    );

    const customerNotes = [
      dev.issueDesc,
      dev.whatHappened ? `\nHow it started: ${dev.whatHappened}` : '',
      dev.whatTried    ? `\nWhat I tried: ${dev.whatTried}` : '',
    ].join('');

    const payload = {
      customer: { first_name: cust.firstName, last_name: cust.lastName, phone: cust.phone, email: cust.email },
      location: { id: 'mail-in', location_id: 'temecula', location_name: 'Mail-In Repair (Temecula)', address: '30401 Cupeno Ln, Temecula, CA 92592' },
      appointment: { preferred_date: '', preferred_time: '', type: 'mail-in' },
      selected_services: [{
        service_id: dev.serviceId || 'mail-in-inquiry',
        service_name: dev.serviceName || 'Mail-In Repair',
        price: dev.servicePrice,
        quantity: 1,
        what_to_bring: '',
        deviceCategory: dev.categoryId,
      }],
      selected_addons: [],
      selected_bundle: [],
      estimate: { estimated_total: dev.servicePrice ?? 0, bundle_savings: 0 },
      customer_notes: customerNotes,
      shipping_address: returnAddr,
      repairshopr: {
        subject: `Mail-In — ${deviceLabel} — ${dev.serviceName || 'Repair'} — ${cust.firstName} ${cust.lastName}`,
        notes:   notesLines.join('\n'),
        comment: customerNotes,
      },
    };

    let ticketNumber: number | null = null;
    let ticketId: number | null     = null;
    let rsError: string | null      = null;

    try {
      const res  = await fetch('/api/create-ticket', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ payload }),
      });
      const data = await res.json();
      if (data.ok) {
        ticketNumber = data.ticketNumber;
        ticketId     = data.ticketId;
      } else {
        rsError = data.error ?? 'Ticket creation failed';
      }
    } catch (err: any) {
      rsError = err.message ?? 'Network error';
    }

    // Notifications — fire and forget
    fetch('/api/intake-notify', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ payload, ticketNumber, ticketId, rsError }),
    }).catch(() => {});

    setSubmitted({ ticketNumber, fallbackId, deviceLabel, serviceName: dev.serviceName });
    setSubmitting(false);
    document.getElementById('mail-in-intake')?.scrollIntoView({ behavior: 'smooth' });
  }

  // ─── Shared nav buttons ────────────────────────────────────────────────────

  function NavButtons({ onNext, nextLabel = 'Continue' }: { onNext?: () => void; nextLabel?: string }) {
    return (
      <div className="flex items-center gap-3 pt-2">
        {step > 1 && (
          <button type="button" onClick={prevStep}
            className="flex items-center gap-2 border border-gray-700 hover:border-gray-500 text-[#B0B0B0] hover:text-[#E0E0E0] font-semibold px-5 py-3 rounded-xl transition-colors">
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
        )}
        <button type="button" onClick={onNext ?? nextStep}
          disabled={submitting}
          className="flex-1 sm:flex-none bg-[#39FF14] hover:bg-[#00BFFF] text-[#0D0D0D] font-bold px-8 py-3 rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
          {submitting ? <><Loader className="h-4 w-4 animate-spin" /> Submitting…</> : <>{nextLabel} <ArrowRight className="h-4 w-4" /></>}
        </button>
      </div>
    );
  }

  // ─── JSX ──────────────────────────────────────────────────────────────────

  return (
    <div className="bg-[#0D0D0D] text-[#E0E0E0] min-h-screen">
      <SEOHead
        title="Mail-In PS5 Controller Upgrade Service | Nationwide | GameFix Solutions"
        description="Send your PS5 DualSense from anywhere in the US for professional TMR joystick upgrades, mouse click triggers, stick drift repair, and more. Fast turnaround, 90-day warranty."
        keywords="mail in ps5 controller upgrade, ps5 stick drift repair mail in, nationwide ps5 repair, ps5 tmr upgrade service, dualsense repair mail in"
        canonical="https://gamefixsolutions.com/mail-in-repairs"
      />

      {/* ── Hero ── */}
      <section className="py-20 px-4 bg-gradient-to-br from-[#0D0D0D] via-gray-900 to-[#0D0D0D] border-b border-gray-800">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-[#39FF14]/10 border border-[#39FF14]/30 rounded-full px-4 py-2 mb-6">
            <Package className="h-4 w-4 text-[#39FF14]" />
            <span className="text-[#39FF14] text-sm font-semibold">Nationwide Mail-In Repair</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
            Expert Repairs.<br />
            <span className="text-[#00BFFF]">Shipped Back Fast.</span>
          </h1>
          <p className="text-xl text-[#B0B0B0] max-w-2xl mx-auto mb-10">
            PS5 controllers, HDMI ports, Nintendo Switch — we fix it all and ship it back, often faster than the big names charge more to do. No wait list. No weeks of downtime.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={scrollToIntake}
              className="bg-[#39FF14] hover:bg-[#00BFFF] text-[#0D0D0D] font-bold px-8 py-4 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2">
              Start Your Mail-In <ArrowRight className="h-5 w-5" />
            </button>
            <Link to="/custom-controllers"
              className="border border-[#00BFFF] text-[#00BFFF] hover:bg-[#00BFFF]/10 font-semibold px-8 py-4 rounded-lg transition-colors duration-200 text-center">
              See Custom Controllers
            </Link>
          </div>
        </div>
      </section>

      {/* ── Stats ── */}
      <section className="py-10 px-4 border-b border-gray-800 bg-gray-900/40">
        <div className="max-w-4xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8">
          <StatBadge value="2–4 days" label="Average turnaround" />
          <StatBadge value="From $60" label="Stick drift repair" />
          <StatBadge value="$160" label="PS5 HDMI repair" />
          <StatBadge value="90-day" label="Warranty included" />
        </div>
      </section>

      {/* ── Comparison ── */}
      <section className="py-16 px-4 border-b border-gray-800">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold mb-3">Why Ship to Us vs. Cinch, Hex, or Battle Beaver?</h2>
            <p className="text-[#B0B0B0] max-w-2xl mx-auto">
              The big custom controller brands do great work — but they're also juggling thousands of orders. We keep our queue tight so your gear doesn't sit on a shelf for three weeks.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left py-3 px-4 text-[#B0B0B0] font-medium"></th>
                  <th className="py-3 px-4 text-[#39FF14] font-bold text-center">GameFix SD</th>
                  <th className="py-3 px-4 text-[#B0B0B0] font-medium text-center">Cinch / Hex / Aim</th>
                  <th className="py-3 px-4 text-[#B0B0B0] font-medium text-center">Battle Beaver</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {[
                  ['Stick drift repair (standard controller)', '✅ $60', '✅ $150–$200+', '✅ $150+'],
                  ['Controller build turnaround', '✅ 2–4 days', '⏳ 2–6 weeks', '⏳ 2–4 weeks'],
                  ['PS5 HDMI port repair', '✅ $160', '❌ Not offered', '❌ Not offered'],
                  ['PS5 hardware repairs', '✅ Yes', '❌ No', '❌ No'],
                  ['Nintendo Switch repairs', '✅ Yes', '❌ No', '❌ No'],
                  ['Personal communication', '✅ Direct text/call', '❌ Ticket system', '❌ Ticket system'],
                ].map(([feature, us, cinch, bb]) => (
                  <tr key={feature} className="hover:bg-gray-900/40">
                    <td className="py-3 px-4 text-[#E0E0E0]">{feature}</td>
                    <td className="py-3 px-4 text-center font-semibold text-[#39FF14]">{us}</td>
                    <td className="py-3 px-4 text-center text-[#B0B0B0]">{cinch}</td>
                    <td className="py-3 px-4 text-center text-[#B0B0B0]">{bb}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ── PS5 Controller section ── */}
      <section className="py-20 px-4 border-b border-gray-800">
        <div className="max-w-4xl mx-auto">
          <Badge text="PS5 Controller · Mail-In" />
          <div className="flex items-start gap-4 mb-6">
            <div className="bg-[#FF1744]/10 p-4 rounded-xl shrink-0">
              <Cpu className="h-8 w-8 text-[#FF1744]" />
            </div>
            <div>
              <h2 className="text-3xl font-bold mb-2">PS5 Stick Drift? Upgrade to TMR Hall Effect Joysticks</h2>
              <p className="text-[#B0B0B0]">The permanent fix — not a band-aid.</p>
            </div>
          </div>
          <div className="prose prose-invert max-w-none text-[#B0B0B0] space-y-4 mb-8">
            <p>If your PS5 DualSense controller is drifting — camera spinning on its own, characters moving without input — you're not imagining it. The stock joystick sensors wear out over time, and Sony's fix is just to swap in another sensor that will drift again.</p>
            <p>We install <strong className="text-[#E0E0E0]">TMR Hall Effect joysticks</strong>, which use magnetic technology instead of physical contact points. That means <strong className="text-[#E0E0E0]">no wear, no drift</strong> — ever.</p>
            <p>Want to go further? We can add <Link to="/custom-controllers" className="text-[#39FF14] hover:text-[#00BFFF] underline underline-offset-2 transition-colors">clicky triggers, back paddles, or a full custom shell</Link> at the same time — saving you a second round-trip.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
            <RepairCard icon={Cpu} title="Stick Drift Repair — Standard Controller" price="$60" time="2–4 days after receipt" link="/custom-controllers" />
            <RepairCard icon={Cpu} title="Stick Drift Repair — Pro Controller (Scuf, Aim, Hex, BB)" price="$80" time="2–4 days after receipt" link="/custom-controllers" />
            <RepairCard icon={Zap} title="GuliKit TMR Upgrade (add to any tier)" price="+$20" time="Same turnaround" link="/custom-controllers" />
            <RepairCard icon={Wrench} title="Add Clicky Triggers" price="Quote" time="Combined with stick job" link="/custom-controllers" />
            <RepairCard icon={Gamepad2} title="Add Back Paddles" price="Quote" time="Combined with stick job" link="/custom-controllers" />
          </div>
          <div className="bg-gray-900 border border-[#39FF14]/30 rounded-xl p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex-1">
              <h3 className="font-bold text-lg mb-1">Ready to end drift for good?</h3>
              <p className="text-[#B0B0B0] text-sm">Starting at $60 · +$20 for GuliKit TMR. Ship it, we fix it in 2–4 days and ship it back — return shipping included. We invoice when it's done.</p>
            </div>
            <button onClick={scrollToIntake} className="bg-[#39FF14] hover:bg-[#00BFFF] text-[#0D0D0D] font-bold px-6 py-3 rounded-lg transition-colors whitespace-nowrap shrink-0">
              Start Mail-In
            </button>
          </div>
        </div>
      </section>

      {/* ── PS5 HDMI section ── */}
      <section className="py-20 px-4 bg-gray-900/20 border-b border-gray-800">
        <div className="max-w-4xl mx-auto">
          <Badge text="PlayStation 5 · Mail-In" />
          <div className="flex items-start gap-4 mb-6">
            <div className="bg-[#00BFFF]/10 p-4 rounded-xl shrink-0">
              <Gamepad2 className="h-8 w-8 text-[#00BFFF]" />
            </div>
            <div>
              <h2 className="text-3xl font-bold mb-2">PS5 HDMI Port & Common PS5 Repairs</h2>
              <p className="text-[#B0B0B0]">No picture? Overheating? We've seen it — and fixed it.</p>
            </div>
          </div>
          <div className="prose prose-invert max-w-none text-[#B0B0B0] space-y-4 mb-8">
            <p>The PS5's HDMI port is notoriously fragile. A cable gets pulled, a TV gets moved, and suddenly you've got no signal, a bent port, or broken pins. This is a <strong className="text-[#E0E0E0]">board-level microsoldering repair</strong> — not something a big-box store will touch — and it's one of our most common jobs.</p>
            <p>We also handle overheating (liquid metal reapplication + deep clean), ripped fan connectors from DIY attempts, disc drive issues, and no-power diagnostics. If another shop said it was unfixable, send it to us.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <RepairCard icon={Zap}      title="HDMI Port Replacement"      price="$160"     time="2–4 days" />
            <RepairCard icon={Wrench}   title="Overheating / Liquid Metal" price="$160"     time="2–4 days" />
            <RepairCard icon={Cpu}      title="Ripped Fan Connector"       price="$150"     time="2–4 days" />
            <RepairCard icon={Gamepad2} title="Disc Drive Repair"          price="$155"     time="2–4 days" />
            <RepairCard icon={Shield}   title="No Power Diagnostic"        price="From $75" time="2–4 days" />
            <RepairCard icon={Wrench}   title="Power Supply"               price="$145"     time="2–4 days" />
          </div>
          <div className="bg-[#39FF14]/5 border border-[#39FF14]/20 rounded-xl p-4 flex items-center gap-4 mb-8">
            <div className="bg-[#39FF14]/10 p-2.5 rounded-lg shrink-0">
              <Wrench className="h-5 w-5 text-[#39FF14]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[#E0E0E0] font-semibold text-sm">Add Deep Cleaning — +$30</p>
              <p className="text-xs text-[#B0B0B0] mt-0.5">Recommended with any HDMI repair. Removes dust buildup that causes overheating and extends console life.</p>
            </div>
            <span className="text-[#39FF14] font-bold shrink-0">+$30</span>
          </div>
          <div className="bg-gray-900 border border-[#00BFFF]/30 rounded-xl p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex-1">
              <h3 className="font-bold text-lg mb-1">Don't let a broken HDMI port bench your PS5.</h3>
              <p className="text-[#B0B0B0] text-sm">Box it up, ship it out, play again in days — not months.</p>
            </div>
            <button onClick={scrollToIntake} className="bg-[#00BFFF] hover:bg-[#39FF14] text-[#0D0D0D] font-bold px-6 py-3 rounded-lg transition-colors whitespace-nowrap shrink-0">
              Start Mail-In
            </button>
          </div>
        </div>
      </section>

      {/* ── Switch section ── */}
      <section className="py-20 px-4 border-b border-gray-800">
        <div className="max-w-4xl mx-auto">
          <Badge text="Nintendo Switch · Mail-In" />
          <div className="flex items-start gap-4 mb-6">
            <div className="bg-[#FF1744]/10 p-4 rounded-xl shrink-0">
              <Gamepad2 className="h-8 w-8 text-[#FF1744]" />
            </div>
            <div>
              <h2 className="text-3xl font-bold mb-2">Nintendo Switch Common Repairs</h2>
              <p className="text-[#B0B0B0]">Cracked screen, won't charge, won't dock — we fix them all.</p>
            </div>
          </div>
          <div className="prose prose-invert max-w-none text-[#B0B0B0] space-y-4 mb-8">
            <p>The Nintendo Switch takes a beating — it's a handheld that also lives in a dock. Cracked screens, USB-C charging port damage, and Joy-Con drift are the most common issues we see. The OLED model brings its own quirks too.</p>
            <p>We replace LCD and OLED screens with tempered glass, repair or replace USB-C ports, fix M92 and BQ charging IC chips for no-power issues, and resolve docking problems. We work on Switch, Switch Lite, Switch OLED, and the new Switch 2.</p>
            <div className="bg-[#39FF14]/8 border border-[#39FF14]/30 rounded-xl p-4 not-prose">
              <p className="text-[#39FF14] font-semibold text-sm mb-1">No More Back and Forth on No-Power Repairs</p>
              <p className="text-[#B0B0B0] text-sm">Most Switch no-power / won't-charge repairs require the same set of components: USB-C port, M92 power IC, BQ charging IC, and P13 USB controller. Rather than quote each piece separately, we quote one flat rate covering all commonly needed parts — so your Switch gets fixed right, the first time.</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
            <RepairCard icon={Zap}    title="Screen Replacement (LCD/OLED)"                        price="From $120" time="2–4 days" />
            <RepairCard icon={Cpu}    title="No Power / Won't Charge (USB-C + M92 + BQ + P13)"    price="$135"      time="2–4 days" />
            <RepairCard icon={Wrench} title="Won't Dock to TV"                                     price="$135"      time="2–4 days" />
            <RepairCard icon={Shield} title="Battery Replacement"                                  price="$90"       time="2–4 days" />
            <RepairCard icon={Wrench} title="Internal Cleaning"                                    price="$75"       time="2–4 days" />
            <RepairCard icon={Cpu}    title="Switch 2 USB-C Port"                                  price="Call"      time="Contact us for quote" />
          </div>
          <div className="bg-gray-900 border border-[#FF1744]/30 rounded-xl p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex-1">
              <h3 className="font-bold text-lg mb-1">Get your Switch back in the game.</h3>
              <p className="text-[#B0B0B0] text-sm">Mail it in, we'll fix it fast and ship it back fully tested.</p>
            </div>
            <button onClick={scrollToIntake} className="bg-[#FF1744] hover:bg-[#39FF14] text-[#0D0D0D] font-bold px-6 py-3 rounded-lg transition-colors whitespace-nowrap shrink-0">
              Start Mail-In
            </button>
          </div>
        </div>
      </section>

      {/* ── Buyback ── */}
      <section className="py-16 px-4 border-b border-gray-800 bg-gradient-to-r from-[#B8860B]/5 via-[#0D0D0D] to-[#B8860B]/5">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-start gap-4 mb-6">
            <div className="bg-[#FFD700]/10 p-4 rounded-xl shrink-0">
              <Package className="h-8 w-8 text-[#FFD700]" />
            </div>
            <div>
              <h2 className="text-3xl font-bold mb-2">Shipping Us a Repair? Throw In Your Broken Devices</h2>
              <p className="text-[#B0B0B0]">Turn broken devices into credit toward your repair — no separate trip required.</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
            <div className="text-[#B0B0B0] space-y-4">
              <p>Already shipping us your PS5 or Switch? This is the perfect time to clean out any broken or unused devices sitting in a drawer. Include them in the same box and we'll assess their value and <strong className="text-[#E0E0E0]">apply the credit directly to your repair invoice</strong> — reducing what you owe when the job is done.</p>
              <p>We accept devices in any condition — working, broken, cracked, non-functioning, or missing parts. We'll tell you the value before we finalize anything.</p>
              <div className="bg-[#FFD700]/10 border border-[#FFD700]/30 rounded-xl p-4 text-sm">
                <p className="text-[#FFD700] font-semibold mb-1">How it works</p>
                <p className="text-[#B0B0B0]">Pack your broken devices in the same box as your repair. We receive everything, assess the buyback value, and subtract it from your repair total before we invoice you. Simple.</p>
              </div>
            </div>
            <div>
              <p className="text-sm font-semibold text-[#B0B0B0] uppercase tracking-wide mb-4">Devices We Buy</p>
              <div className="grid grid-cols-2 gap-2">
                {['iPhone (any model)', 'iPad (any model)', 'MacBook', 'Nintendo Switch', 'Nintendo Switch OLED', 'Nintendo Switch Lite', 'Nintendo 3DS / 2DS', 'Game Boy Color', 'Game Boy Advance / SP', 'Steam Deck', 'ROG Ally', 'Other handhelds'].map(d => (
                  <div key={d} className="flex items-center gap-2 text-sm text-[#B0B0B0]">
                    <CheckCircle className="h-3.5 w-3.5 text-[#FFD700] shrink-0" />
                    <span>{d}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-[#B0B0B0] mt-4 italic">Working or broken — we buy them all. Credit applied before your invoice.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── How it works ── */}
      <section className="py-20 px-4 border-b border-gray-800 bg-gray-900/20">
        <div className="max-w-4xl mx-auto text-center mb-12">
          <h2 className="text-3xl font-bold mb-3">How Mail-In Works</h2>
          <p className="text-[#B0B0B0]">Simple, transparent, and fast — no confusing portals.</p>
        </div>
        <div className="max-w-3xl mx-auto grid grid-cols-1 sm:grid-cols-4 gap-6">
          {[
            { step: '1', label: 'Submit Request', desc: 'Fill out our 4-step intake below. You\'ll receive a RepairShopr ticket number and full shipping instructions immediately.' },
            { step: '2', label: 'Ship It',        desc: 'Pack your device well and use a tracked service. Include your ticket number inside the box.' },
            { step: '3', label: 'We Fix It',      desc: 'We notify you when it arrives and when it\'s done. We invoice once the repair is complete — no payment needed to ship.' },
            { step: '4', label: 'Back to You',    desc: 'Fully tested, securely packed, and shipped back — return shipping is on us.' },
          ].map(({ step, label, desc }) => (
            <div key={step} className="text-center">
              <div className="w-12 h-12 rounded-full bg-[#39FF14] text-[#0D0D0D] font-bold text-xl flex items-center justify-center mx-auto mb-4">{step}</div>
              <h3 className="font-semibold text-[#E0E0E0] mb-2">{label}</h3>
              <p className="text-sm text-[#B0B0B0]">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Shipping help ── */}
      <ShippingHelpSection />

      {/* ── Intake form ── */}
      <section id="mail-in-intake" className="py-20 px-4">
        <div className="max-w-2xl mx-auto">

          {submitted ? (
            <ConfirmationView submitted={submitted} cust={cust} dev={dev} ret={ret} />
          ) : (
            <>
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold mb-2">Start Your Mail-In Repair</h2>
                <p className="text-[#B0B0B0]">Complete the form below — you'll receive your ticket number and shipping instructions immediately.</p>
              </div>

              <StepIndicator step={step} />

              <div className="bg-gray-900 border border-gray-700 rounded-2xl p-6 sm:p-8 space-y-5">

                {/* ── Step 1: Customer Info ── */}
                {step === 1 && (
                  <>
                    <div className="flex items-center gap-3 mb-6">
                      <div className="bg-[#39FF14]/10 p-2.5 rounded-lg">
                        <User className="h-5 w-5 text-[#39FF14]" />
                      </div>
                      <div>
                        <h3 className="font-bold text-[#E0E0E0]">Your Information</h3>
                        <p className="text-xs text-[#B0B0B0]">We'll use this to create your repair ticket and send you a confirmation</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className={lbl}>First Name</label>
                        <input value={cust.firstName} onChange={e => setC('firstName', e.target.value)}
                          className={inp(errors.firstName)} placeholder="Jane" />
                        <FieldErr msg={errors.firstName} />
                      </div>
                      <div>
                        <label className={lbl}>Last Name</label>
                        <input value={cust.lastName} onChange={e => setC('lastName', e.target.value)}
                          className={inp(errors.lastName)} placeholder="Smith" />
                        <FieldErr msg={errors.lastName} />
                      </div>
                    </div>

                    <div>
                      <label className={lbl}>Phone Number</label>
                      <div className="relative">
                        <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
                        <input value={cust.phone} onChange={e => setC('phone', e.target.value)}
                          className={`${inp(errors.phone)} pl-10`} placeholder="(760) 555-0100" type="tel" />
                      </div>
                      <FieldErr msg={errors.phone} />
                    </div>

                    <div>
                      <label className={lbl}>Email Address</label>
                      <div className="relative">
                        <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
                        <input value={cust.email} onChange={e => setC('email', e.target.value)}
                          className={`${inp(errors.email)} pl-10`} placeholder="jane@example.com" type="email" />
                      </div>
                      <FieldErr msg={errors.email} />
                    </div>

                    <NavButtons />
                  </>
                )}

                {/* ── Step 2: Device & Issue ── */}
                {step === 2 && (
                  <>
                    <div className="flex items-center gap-3 mb-6">
                      <div className="bg-[#00BFFF]/10 p-2.5 rounded-lg">
                        <Wrench className="h-5 w-5 text-[#00BFFF]" />
                      </div>
                      <div>
                        <h3 className="font-bold text-[#E0E0E0]">Device & Issue</h3>
                        <p className="text-xs text-[#B0B0B0]">Tell us about the device and what's going on</p>
                      </div>
                    </div>

                    <div>
                      <label className={lbl}>Device Type</label>
                      <select value={dev.categoryId} onChange={e => handleCatChange(e.target.value)}
                        className={inp(errors.categoryId)}>
                        <option value="">Select device type…</option>
                        {categoriesForSelect.map(([id, label]) => (
                          <option key={id} value={id}>{label}</option>
                        ))}
                      </select>
                      <FieldErr msg={errors.categoryId} />
                    </div>

                    <div>
                      <label className={lbl}>Model <span className="text-gray-600 font-normal">(optional)</span></label>
                      <input value={dev.model} onChange={e => setD('model', e.target.value)}
                        className={inp()} placeholder="e.g. DualSense, Switch OLED, iPhone 15 Pro" />
                    </div>

                    <div>
                      <label className={lbl}>
                        Service / Repair Needed <span className="text-gray-600 font-normal">(optional — select if you know it)</span>
                      </label>
                      {servicesForCat.length > 0 ? (
                        <select value={dev.serviceId} onChange={e => handleServiceChange(e.target.value)}
                          className={inp()}>
                          <option value="">Select a service…</option>
                          {servicesForCat.map(s => (
                            <option key={s.id} value={s.id}>
                              {s.repairType}{s.basePrice !== null ? ` — $${s.basePrice}` : ' — Quote'}
                            </option>
                          ))}
                          <option value="other">Other / Not sure</option>
                        </select>
                      ) : (
                        <input value={dev.serviceName} onChange={e => setD('serviceName', e.target.value)}
                          className={inp()} placeholder="e.g. HDMI port, stick drift, screen replacement…" />
                      )}
                    </div>

                    <div>
                      <label className={lbl}>Issue Description <span className="text-[#FF1744]">*</span></label>
                      <textarea value={dev.issueDesc} onChange={e => setD('issueDesc', e.target.value)}
                        rows={3} className={`${inp(errors.issueDesc)} resize-none`}
                        placeholder="Describe what's wrong — the more detail, the faster we can quote you." />
                      <FieldErr msg={errors.issueDesc} />
                    </div>

                    <div>
                      <label className={lbl}>How did it start? <span className="text-gray-600 font-normal">(optional)</span></label>
                      <textarea value={dev.whatHappened} onChange={e => setD('whatHappened', e.target.value)}
                        rows={2} className={`${inp()} resize-none`}
                        placeholder="e.g. Dropped it, cable got yanked, started randomly…" />
                    </div>

                    <div>
                      <label className={lbl}>What have you already tried? <span className="text-gray-600 font-normal">(optional)</span></label>
                      <textarea value={dev.whatTried} onChange={e => setD('whatTried', e.target.value)}
                        rows={2} className={`${inp()} resize-none`}
                        placeholder="e.g. Factory reset, different cable, another shop…" />
                    </div>

                    <NavButtons />
                  </>
                )}

                {/* ── Step 3: Return Shipping ── */}
                {step === 3 && (
                  <>
                    <div className="flex items-center gap-3 mb-6">
                      <div className="bg-[#FF9800]/10 p-2.5 rounded-lg">
                        <Truck className="h-5 w-5 text-[#FF9800]" />
                      </div>
                      <div>
                        <h3 className="font-bold text-[#E0E0E0]">Return Shipping Address</h3>
                        <p className="text-xs text-[#B0B0B0]">This is the address we will use to return your repaired device</p>
                      </div>
                    </div>

                    <label className="flex items-start gap-3 cursor-pointer">
                      <input type="checkbox" checked={ret.sameAsContact} onChange={e => handleSameAsContact(e.target.checked)}
                        className="mt-0.5 w-4 h-4 accent-[#39FF14] cursor-pointer" />
                      <span className="text-sm text-[#B0B0B0]">
                        My name and phone number are the same as my contact info above
                        {ret.sameAsContact && <span className="text-[#39FF14] ml-1">(pre-filled below)</span>}
                      </span>
                    </label>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className={ret.sameAsContact ? 'sm:col-span-2' : ''}>
                        <label className={lbl}>Full Name</label>
                        <input value={ret.fullName} onChange={e => setR('fullName', e.target.value)}
                          className={inp(errors.fullName)} placeholder="Jane Smith"
                          readOnly={ret.sameAsContact} />
                        <FieldErr msg={errors.fullName} />
                      </div>
                      {!ret.sameAsContact && (
                        <div>
                          <label className={lbl}>Phone Number</label>
                          <input value={ret.phone} onChange={e => setR('phone', e.target.value)}
                            className={inp(errors.phone)} placeholder="(760) 555-0100" type="tel" />
                          <FieldErr msg={errors.phone} />
                        </div>
                      )}
                    </div>

                    <div>
                      <label className={lbl}>Street Address</label>
                      <div className="relative">
                        <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
                        <input value={ret.address1} onChange={e => setR('address1', e.target.value)}
                          className={`${inp(errors.address1)} pl-10`} placeholder="123 Main St" />
                      </div>
                      <FieldErr msg={errors.address1} />
                    </div>

                    <div>
                      <label className={lbl}>Address Line 2 <span className="text-gray-600 font-normal">(apt, unit, suite…)</span></label>
                      <input value={ret.address2} onChange={e => setR('address2', e.target.value)}
                        className={inp()} placeholder="Apt 4B" />
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                      <div className="col-span-2 sm:col-span-1">
                        <label className={lbl}>City</label>
                        <input value={ret.city} onChange={e => setR('city', e.target.value)}
                          className={inp(errors.city)} placeholder="San Diego" />
                        <FieldErr msg={errors.city} />
                      </div>
                      <div>
                        <label className={lbl}>State</label>
                        <input value={ret.state} onChange={e => setR('state', e.target.value)}
                          className={inp(errors.state)} placeholder="CA" maxLength={2} />
                        <FieldErr msg={errors.state} />
                      </div>
                      <div>
                        <label className={lbl}>ZIP Code</label>
                        <input value={ret.zip} onChange={e => setR('zip', e.target.value)}
                          className={inp(errors.zip)} placeholder="92101" inputMode="numeric" maxLength={10} />
                        <FieldErr msg={errors.zip} />
                      </div>
                    </div>

                    {ret.sameAsContact && (
                      <p className="text-xs text-[#B0B0B0] bg-gray-800/60 rounded-lg px-4 py-2.5">
                        Phone number from Step 1 will be used: <span className="text-[#E0E0E0]">{cust.phone}</span>
                      </p>
                    )}

                    <NavButtons />
                  </>
                )}

                {/* ── Step 4: Review & Submit ── */}
                {step === 4 && (
                  <>
                    <div className="flex items-center gap-3 mb-6">
                      <div className="bg-[#39FF14]/10 p-2.5 rounded-lg">
                        <CheckCircle className="h-5 w-5 text-[#39FF14]" />
                      </div>
                      <div>
                        <h3 className="font-bold text-[#E0E0E0]">Review & Submit</h3>
                        <p className="text-xs text-[#B0B0B0]">Confirm your details before we create your ticket</p>
                      </div>
                    </div>

                    <div className="space-y-4 text-sm">
                      {/* Customer */}
                      <div className="bg-gray-800/60 rounded-xl p-4">
                        <p className="text-xs font-semibold text-[#B0B0B0] uppercase tracking-widest mb-3">Customer</p>
                        <div className="grid grid-cols-2 gap-y-2 gap-x-4">
                          <div><span className="text-gray-500">Name</span></div>
                          <div className="text-[#E0E0E0] font-medium">{cust.firstName} {cust.lastName}</div>
                          <div><span className="text-gray-500">Phone</span></div>
                          <div className="text-[#E0E0E0]">{cust.phone}</div>
                          <div><span className="text-gray-500">Email</span></div>
                          <div className="text-[#E0E0E0] break-all">{cust.email}</div>
                        </div>
                      </div>

                      {/* Device */}
                      <div className="bg-gray-800/60 rounded-xl p-4">
                        <p className="text-xs font-semibold text-[#B0B0B0] uppercase tracking-widest mb-3">Device</p>
                        <div className="grid grid-cols-2 gap-y-2 gap-x-4">
                          <div><span className="text-gray-500">Type</span></div>
                          <div className="text-[#E0E0E0] font-medium">{DEVICE_CATEGORY_LABELS[dev.categoryId as keyof typeof DEVICE_CATEGORY_LABELS] || dev.categoryId}</div>
                          {dev.model && <><div><span className="text-gray-500">Model</span></div><div className="text-[#E0E0E0]">{dev.model}</div></>}
                          <div><span className="text-gray-500">Service</span></div>
                          <div className="text-[#E0E0E0]">{dev.serviceName || <span className="text-gray-500 italic">To be determined</span>}</div>
                          {dev.servicePrice !== null && <><div><span className="text-gray-500">Est. Price</span></div><div className="text-[#39FF14] font-bold">${dev.servicePrice}</div></>}
                        </div>
                      </div>

                      {/* Issue */}
                      <div className="bg-gray-800/60 rounded-xl p-4">
                        <p className="text-xs font-semibold text-[#B0B0B0] uppercase tracking-widest mb-2">Issue Description</p>
                        <p className="text-[#E0E0E0]">{dev.issueDesc}</p>
                        {dev.whatHappened && <><p className="text-xs text-gray-500 mt-3 mb-1">How it started</p><p className="text-[#B0B0B0]">{dev.whatHappened}</p></>}
                        {dev.whatTried    && <><p className="text-xs text-gray-500 mt-3 mb-1">What was tried</p><p className="text-[#B0B0B0]">{dev.whatTried}</p></>}
                      </div>

                      {/* Return shipping */}
                      <div className="bg-gray-800/60 rounded-xl p-4">
                        <p className="text-xs font-semibold text-[#B0B0B0] uppercase tracking-widest mb-3">Return Shipping Address</p>
                        <p className="text-[#E0E0E0] font-medium">{ret.sameAsContact ? `${cust.firstName} ${cust.lastName}` : ret.fullName}</p>
                        <p className="text-[#B0B0B0]">{ret.address1}{ret.address2 ? `, ${ret.address2}` : ''}</p>
                        <p className="text-[#B0B0B0]">{ret.city}, {ret.state} {ret.zip}</p>
                        <p className="text-[#B0B0B0]">{ret.sameAsContact ? cust.phone : ret.phone}</p>
                      </div>
                    </div>

                    <div className="bg-[#39FF14]/8 border border-[#39FF14]/20 rounded-xl px-4 py-3 text-sm text-[#B0B0B0]">
                      <span className="text-[#39FF14] font-semibold">After submitting:</span> You'll receive a RepairShopr ticket number, a confirmation email, and shipping instructions. No payment is required before shipping.
                    </div>

                    <NavButtons nextLabel="Submit Mail-In Repair Request" onNext={handleSubmit} />
                  </>
                )}
              </div>

              {/* Trust badges */}
              <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
                {[
                  { icon: Clock,        label: '2–4 day turnaround',        sub: 'Most repairs' },
                  { icon: Package,      label: 'Return shipping included',   sub: 'We cover it' },
                  { icon: Shield,       label: 'Tested before ship',         sub: 'Every repair verified' },
                  { icon: CheckCircle,  label: 'Invoice after repair',       sub: 'No upfront payment' },
                ].map(({ icon: Icon, label, sub }) => (
                  <div key={label} className="flex flex-col items-center gap-2">
                    <Icon className="h-6 w-6 text-[#39FF14]" />
                    <p className="font-semibold text-sm text-[#E0E0E0]">{label}</p>
                    <p className="text-xs text-[#B0B0B0]">{sub}</p>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
